import { MouseEvent as ReactMouseEvent, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useQuery } from "@apollo/client";

import { CLIP_COMMENTS } from "MVVM/GraphQL/clipComments";
import { IClipComment } from "MVVM/Components/Comments/Clip/@types";

interface ITopCommentProps {
  clipId: string;
  onOpen: () => void;
}

const ROTATE = 5;
const INTERVAL_MS = 7000;

// Fixed-size comment strip shown over the video, above the stats. Cycles
// through the latest few comments (fade only, the box never resizes). Clicking
// opens the clip modal on the comments tab. Hidden when there are no comments.
export const TopComment = ({ clipId, onOpen }: ITopCommentProps) => {
  const { data } = useQuery(CLIP_COMMENTS, {
    variables: {
      id: clipId,
      first: ROTATE,
      after: null,
      last: null,
      before: null,
    },
    fetchPolicy: "cache-and-network",
  });

  const nodes: IClipComment[] = data?.clip?.comments?.nodes ?? [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (nodes.length <= 1) return;
    const id = setInterval(
      () => setIdx((i) => (i + 1) % nodes.length),
      INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, [nodes.length]);

  if (!nodes.length) return null;

  const top = nodes[idx % nodes.length];

  const handleClick = (event: ReactMouseEvent) => {
    event.stopPropagation();
    onOpen();
  };

  return (
    <Strip type="button" onClick={handleClick}>
      <Fade key={top.id}>
        <Avatar src={top.user?.avatarUrl} alt="" />
        <Body>
          <Name>{top.user?.username}</Name>
          {top.gifUrl ? (
            <GifThumb src={top.gifUrl} alt="gif" loading="lazy" />
          ) : (
            <Message>{top.text}</Message>
          )}
        </Body>
      </Fade>
    </Strip>
  );
};

const Strip = styled.button`
  align-items: center;
  backdrop-filter: blur(4px);
  background: rgba(10, 14, 18, 0.68);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  height: 56px;
  overflow: hidden;
  padding: 0 14px;
  transition: background 0.15s ease;
  width: min(320px, 100%);

  &:hover {
    background: rgba(10, 14, 18, 0.82);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Fade = styled.span`
  align-items: center;
  animation: ${fadeIn} 0.4s ease;
  display: flex;
  gap: 10px;
  min-width: 0;
  width: 100%;
`;

const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 32px;
  object-fit: cover;
  width: 32px;
`;

const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  text-align: left;
`;

const Name = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Message = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12.5px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GifThumb = styled.img`
  border-radius: 6px;
  height: 30px;
  max-width: 90px;
  object-fit: cover;
`;
